import { IAuthTokenResultDto, IAuthAccount, IJwtAuthTokenResult } from "../src/taskpane/interfaces/IAuthTokenResultDto";
import rabbit = require("crypto-js/rabbit");
import utf8 = require("crypto-js/enc-utf8");
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc' // import plugin
import { logger } from "./logger";
import { axiosRetryPost } from "./AxiosRetryUtil";
import generalModule from "./GeneralModule";
import { shared } from "../src/taskpane/config/constants";
import { StorageHelper } from "./StorageHelper";
import { ITokenRequest, ITokenResponse } from "../src/taskpane/components/_v2/redux/api/nintex/api-nintex-interface";
import { apiAquireFlowToken, apiAquireMailSendToken, apiAquireMailboxToken, apiAquireSharePointAdminToken, apiCheckIsTessAuthenticated, apiRefreshTokenRequest, apiSilentTokenRequest, refreshJwt } from "../src/taskpane/services/ApiService";
import { clearLocalStorage } from "../src/taskpane/components/helpers/localStorageHelper";
import { notifyCopilotAvailabilityChanged } from "../src/taskpane/services/copilot/copilotAvailability";
import { failedJwtRenewal, isAuthRejection, isUnanswered, ranOutOfTime, wentOffline } from "./authRejection";
import { sameAccountId } from "./sameAccountId";
import { SharedRefreshes, waitForRefresh } from "./SharedRefreshes";

/** The token refreshes that are shared (SharedRefreshes): the scheduled or forced refresh of the
 * Graph, SharePoint, OneDrive and Teams tokens together, the Flow token, the Mail.Send token. */
type RefreshKind = "auth" | "flow" | "mailSend";

/** How a scheduled refresh's token call ended: its answer, or what it failed with. */
type RefreshOutcome = { value: IAuthTokenResultDto } | { error: any };

/** A refresh's token call, told whether its answer is still wanted: once it isn't, the call isn't
 * asked again (ApiService, 2026-10-28). */
type RefreshCall = (stillWanted: () => boolean) => Promise<IAuthTokenResultDto>;

/** The JWT a request was sent with: the request interceptor's "Authorization: Bearer …" header
 * (axios 0.28 keeps headers as a plain object). */
const bearerToken = (headers: any): string | undefined => {
    const value: unknown = headers?.Authorization;
    return typeof value === "string" && value.indexOf("Bearer ") === 0 ? value.substring("Bearer ".length) : undefined;
};

dayjs.extend(utc);

enum TokenStorage {
    Local,
    Roaming
}

export enum TokenResource {
    All,
    Graph,
    SharePoint,
    OneDrive,
    Flow,
    SharePointAdmin,
    MailSend,
    Nintex,
    Mailbox,
    Teams,
    Tess
}

export enum DialogMessageStatus {
    Success,
    Failure
}

export interface DialogMessageResponse {
    status: DialogMessageStatus;
    result: IAuthTokenResultDto | null;
}

export class AuthModule {

    private static readonly CypherSecret: string = "xbl001=klr";
    // forceGraphRefresh's limit for its token call, as Copilot's own requests have one
    // (copilotClient's REQUEST_TIMEOUT_MS).
    private static readonly forcedGraphRefreshTimeoutMs: number = 30000;
    // After a scheduled refresh got no answer, the getters keep the state this long before they ask
    // again (review 2026-10-20): while the network is gone, or the API can't be reached, each getter
    // whose token is due would otherwise start, and wait for, another call that can't be answered.
    static readonly unansweredRefreshPauseMs: number = 30000;
    // How long a scheduled refresh's call goes unanswered before it is asked once more (askOnceMore).
    // Its callers wait for the answer however long it takes, as every caller did before 2026-10-26:
    // handed out, expired tokens only fail, and what failed meanwhile stayed broken after the answer
    // came (an empty Sites list, a stored "no duplicate"); and going on with tokens that still
    // worked ran what the app did next during the call, with calls and sign-outs it didn't have
    // before (the second independent review, 2026-10-28).
    private static readonly askOnceMoreAfterMs: number = 90000;

    private static readonly BaseAuthStateKey: string = "AuthState";
    private static AuthStateKey: string = AuthModule.BaseAuthStateKey;
    // forceGraphRefresh's guard: the Graph token (identified by its expiry) it last forced a refresh
    // for. Cleared with everything else by clearLocalStorage on sign-out.
    private static readonly ForcedGraphRefreshKey: string = "docubird-copilot-graph-refresh";

    // The refreshes in flight per kind, each for one account, and who waits for which.
    private readonly sharedRefreshes: SharedRefreshes<RefreshKind> = new SharedRefreshes<RefreshKind>(() => this.getPersistedAuthState());
    // The Docubird JWT renewal in flight, for the JWT it renews: callers holding that JWT share it.
    private jwtRenewal: { token: string; promise: Promise<string> } = null;
    // The account whose scheduled refresh last got no answer, and when (unansweredRefreshPauseMs).
    private unansweredRefresh: { accountId: string; at: number } = null;
    // The refresh call asked again after its own limit ended it, for the state it started from
    // (askUntilAnswered): one at a time per state.
    private askingAgain: { before: IAuthTokenResultDto; outcome: Promise<RefreshOutcome> } = null;
    // The scheduled refresh's call asked once more for the state it started from (askOnceMore).
    private askedOnceMore: { before: IAuthTokenResultDto; outcome: Promise<RefreshOutcome | null> } = null;
    // forceGraphRefresh's guard when localStorage is unavailable.
    private lastForcedGraphRefresh: string = null;
    private globalAuthState: IAuthTokenResultDto;
    private tenantApiKey: string;

    // Serializes roaming saveAsync calls so concurrent persists (multiple token getters refreshing
    // on load) never collide with Outlook's simultaneous-async-call limit, which is the most likely
    // cause of saveAsync failures in new Outlook / Outlook on the web.
    private roamingSaveChain: Promise<unknown> = Promise.resolve();
    // Last logged roaming-failure signature, so a repeating failure is reported once, not every refresh.
    private lastRoamingErrorSignature: string = null;

    private signOutCallback: () => void;
    // Whether this sign-out episode was announced (audit 2026-10-19). Outlook's callback reads the
    // selected emails again, which asks for a Graph token: without a sign-in that called the callback
    // again, for ever. A sign-in (a stored state with an account) ends the episode.
    private signOutNotified: boolean = false;
    // The forced Graph refresh in flight, for the account it refreshes: a second caller for that
    // account shares it instead of asking again.
    private forcedGraphRefresh: { accountId: string; promise: Promise<string | undefined> } = null;

    setSignOutCallback(callback: () => void) {
        this.signOutCallback = callback;
        this.signOutNotified = false;
    }

    // The one way to the sign-out callback: once per sign-out episode.
    private notifySignOut(): void {
        if (this.signOutNotified) {
            return;
        }
        this.signOutNotified = true;
        this.signOutCallback?.();
    }

    private async tryPersistAuthState(storage: TokenStorage, authState: IAuthTokenResultDto): Promise<boolean> {
        try {
            if (storage === TokenStorage.Roaming) {
                if (typeof Office !== "undefined" && typeof Office.context?.diagnostics?.host !== "undefined" && Office.context.requirements.isSetSupported("Mailbox")) {
                    try {
                        Office.context.roamingSettings.set(AuthModule.AuthStateKey, authState);
                    } catch (setError) {
                        // e.g. error 9057 when the serialized auth state exceeds the 32 KB roaming limit.
                        this.logRoamingSaveFailureOnce(setError?.code, setError?.message);
                        return false;
                    }

                    return await this.queueRoamingSave();
                }
            }
            else if (storage === TokenStorage.Local) {
                const storageValue: string = rabbit.encrypt(JSON.stringify(authState), AuthModule.CypherSecret).toString();
                try {
                    localStorage.setItem(AuthModule.AuthStateKey, storageValue);
                } catch (error) {
                    return await StorageHelper.setItemAsync(AuthModule.AuthStateKey, storageValue);
                }
                return true;
            }

        } catch (error) {
            logger(error, "AuthModule.ts (tryPersistAuthState)");
        }

        return false;
    }

    // Runs one roamingSettings.saveAsync at a time. Chaining the saves prevents several concurrent
    // persists (e.g. graph/flow/mailSend tokens all refreshing on load) from issuing simultaneous
    // async Office calls, which new Outlook / Outlook on the web reject once too many are in flight.
    private queueRoamingSave(): Promise<boolean> {
        // Chain on the previous save (which never rejects - see the trailing catch) so at most one
        // saveAsync is ever in flight. The trailing catch also guarantees this promise never rejects,
        // so a synchronous throw from saveAsync can't poison the chain or escape un-deduped.
        const attempt: Promise<boolean> = this.roamingSaveChain
            .then(() => new Promise<Office.AsyncResult<void>>(resolve =>
                Office.context.roamingSettings.saveAsync(resolve)))
            .then(result => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                    this.lastRoamingErrorSignature = null; // a fresh failure episode should be logged again
                    return true;
                }
                this.logRoamingSaveFailureOnce(result.error?.code, result.error?.message);
                return false;
            })
            .catch(error => {
                this.logRoamingSaveFailureOnce(error?.code, error?.message);
                return false;
            });

        this.roamingSaveChain = attempt;
        return attempt;
    }

    // Logs a roaming persistence failure at most once per distinct error, so a persistent failure
    // (which recurs on every token refresh) doesn't flood the logs. Surfaces the real code/message
    // instead of the boolean that previously reached the logger as the string "undefined".
    private logRoamingSaveFailureOnce(code: number | undefined, message: string | undefined) {
        const signature: string = `${code ?? "?"}:${message ?? ""}`;
        if (this.lastRoamingErrorSignature === signature) {
            return;
        }
        this.lastRoamingErrorSignature = signature;
        logger(new Error(`Roaming AuthState persistence failed (code ${code ?? "unknown"}): ${message ?? "unknown"}`), "AuthModule.ts (tryPersistAuthState)");
    }

    async persistAuthState(authState: IAuthTokenResultDto) {
        this.globalAuthState = authState;
        if (authState?.account) {
            this.signOutNotified = false;
        }
        // Local storage (plus the in-memory globalAuthState above) is the authoritative read path for
        // this device, so it must complete before we return. Roaming persistence is a best-effort,
        // cross-device convenience that round-trips to Exchange - it must never block the auth flow or
        // delay a token refresh, so it is fired without awaiting.
        void this.tryPersistAuthState(TokenStorage.Roaming, authState);
        await this.tryPersistAuthState(TokenStorage.Local, authState);
    }

    private tryGetAuthState(storage: TokenStorage): IAuthTokenResultDto {
        try {

            if (storage === TokenStorage.Roaming) {
                if (typeof Office !== "undefined" && typeof Office.context?.diagnostics?.host !== "undefined" && Office.context.requirements.isSetSupported("Mailbox")) {
                    return Office.context.roamingSettings.get(AuthModule.AuthStateKey);
                }
            } else if (storage === TokenStorage.Local) {
                let serializedAuthState: string;
                try {
                    serializedAuthState = localStorage.getItem(AuthModule.AuthStateKey);
                } catch (error) {
                    serializedAuthState = StorageHelper.getItem(AuthModule.AuthStateKey);
                }

                if (serializedAuthState) {
                    return JSON.parse(rabbit.decrypt(serializedAuthState, AuthModule.CypherSecret).toString(utf8));
                }
            }

        } catch (error) {
            if (storage === TokenStorage.Local) {
                // Unreadable: dropped first. Logging reads the stored account, which found the same
                // value and logged again, to a stack overflow (review 2026-10-20).
                this.removeLocalAuthState();
            }
            logger(error, "AuthModule.ts (tryGetAuthState)");
        }

        return null;
    }

    private removeLocalAuthState(): void {
        try {
            localStorage.removeItem(AuthModule.AuthStateKey);
        } catch (error) {
            // Unavailable: the state is in StorageHelper's fallback.
        }
        StorageHelper.removeItem(AuthModule.AuthStateKey);
    }

    getPersistedAuthState(): IAuthTokenResultDto {

        let authState: IAuthTokenResultDto = null;

        if (this.globalAuthState) {
            return this.globalAuthState;
        }
        else {
            try {
                const roamingAuthState: IAuthTokenResultDto = this.tryGetAuthState(TokenStorage.Roaming);
                const localAuthState: IAuthTokenResultDto = this.tryGetAuthState(TokenStorage.Local);
                if (roamingAuthState && localAuthState) {
                    const roamingExpiration = +dayjs.utc(roamingAuthState.graphTokenExpiresOn);
                    const localExpiration = +dayjs.utc(localAuthState.graphTokenExpiresOn);
                    if (roamingExpiration === localExpiration) {
                        authState = roamingAuthState;
                    }
                    else if (localExpiration > roamingExpiration) {
                        authState = localAuthState;
                        this.tryPersistAuthState(TokenStorage.Roaming, authState);
                    }
                    else {
                        authState = roamingAuthState;
                        this.tryPersistAuthState(TokenStorage.Local, authState);
                    }
                }
                else if (roamingAuthState && !localAuthState) {
                    authState = roamingAuthState;
                    this.tryPersistAuthState(TokenStorage.Local, authState);
                } else if (localAuthState && !roamingAuthState) {
                    authState = localAuthState;
                    this.tryPersistAuthState(TokenStorage.Roaming, authState);
                }

                if (authState && authState.account) {
                    this.globalAuthState = authState;
                    this.signOutNotified = false;
                }
                else {
                    authState = null;
                }
            } catch (error) {
                logger(error, "AuthModule.ts (getPersistedAuthState)");
            }
        }

        return authState;
    }

    clearPersistedAuthState() {
        // A new sign-in starts without the old one's pause, or its refreshes in flight: nobody should
        // wait on them, and a late failure of theirs can't sign it out (each checks the state it
        // started from).
        this.unansweredRefresh = null;
        this.askedOnceMore = null;
        this.sharedRefreshes.clear();
        this.forcedGraphRefresh = null;
        this.jwtRenewal = null;
        try {
            if (this.globalAuthState) {
                this.globalAuthState = undefined;
            }

            if (typeof Office !== "undefined" && typeof Office.context?.diagnostics?.host !== "undefined" && Office.context.requirements.isSetSupported("Mailbox")) {
                Office.context.roamingSettings.remove(AuthModule.AuthStateKey);

                Office.context.roamingSettings.saveAsync(function (result) {
                    if (result.status === Office.AsyncResultStatus.Failed) {
                        console.error(`RoamingSettings SaveAsync failed with message: ${result.error.message}`);
                    }
                });
            }

            // clearLocalStorage clears all of localStorage and never throws. Where localStorage is
            // unavailable the state lives in StorageHelper's fallback (tryPersistAuthState): clear
            // that too, or the next read finds it (audit 2026-10-19).
            clearLocalStorage();
            StorageHelper.removeItem(AuthModule.AuthStateKey);
        } catch (error) {
            logger(error, "AuthModule.ts (clearPersistedAuthState)");
        }
    }

    private calculateMinutesToExpiration(tokenExpirationDate: Date): number {
        if (tokenExpirationDate) {
            try {
                const utcNow = dayjs.utc();
                const tokenExpirationUtc = dayjs.utc(tokenExpirationDate);
                const minutesToExpiration = tokenExpirationUtc.diff(utcNow, "minutes");
                return minutesToExpiration;
            } catch (error) {
                logger(error, "AuthModule.ts (calculateMinutesToExpiration)");
            }
        }
        return 0;
    }

    private getTokenExpirationDate(resource: TokenResource, authState: IAuthTokenResultDto): Date {
        let tokenExpirationDate: Date;

        if (authState) {
            try {
                switch (resource as TokenResource) {
                    case TokenResource.All:

                        const expirationUnixTimeValues: number[] = [];

                        if (authState.graphTokenExpiresOn) expirationUnixTimeValues.push(+dayjs.utc(authState.graphTokenExpiresOn));
                        if (authState.sharePointTokenExpiresOn) expirationUnixTimeValues.push(+dayjs.utc(authState.sharePointTokenExpiresOn));
                        if (authState.oneDriveTokenExpiresOn) expirationUnixTimeValues.push(+dayjs.utc(authState.oneDriveTokenExpiresOn));
                        // if (authState.informationProtectionTokenExpiresOn) expirationUnixTimeValues.push(+dayjs.utc(authState.informationProtectionTokenExpiresOn));
                        if (authState.teamsTokenExpiresOn) expirationUnixTimeValues.push(+dayjs.utc(authState.teamsTokenExpiresOn));

                        if (expirationUnixTimeValues.length > 0) {
                            tokenExpirationDate = new Date(Math.min(...expirationUnixTimeValues));
                        }

                        break;
                    case TokenResource.Graph:
                        tokenExpirationDate = authState.graphTokenExpiresOn;
                        break;
                    case TokenResource.SharePoint:
                        tokenExpirationDate = authState.sharePointTokenExpiresOn;
                        break;
                    case TokenResource.OneDrive:
                        tokenExpirationDate = authState.oneDriveTokenExpiresOn;
                        break;
                    case TokenResource.Flow:
                        tokenExpirationDate = authState.flowTokenExpiresOn;
                        break;
                    case TokenResource.SharePointAdmin:
                        tokenExpirationDate = authState.sharePointAdminTokenExpiresOn;
                        break;
                    case TokenResource.MailSend:
                        tokenExpirationDate = authState.mailSendTokenExpiresOn;
                        break;
                    // case TokenResource.Mailbox:
                    //     tokenExpirationDate = authState.mailboxTokenExpiresOn;
                    //     break;
                    case TokenResource.Teams:
                        tokenExpirationDate = authState.teamsTokenExpiresOn;
                        break;
                }
            } catch (error) {
                logger(error, "AuthModule.ts (getTokenExpirationDate)");
            }
        }

        return tokenExpirationDate;
    }

    private tokenRefreshRequired(tokenExpirationDate: Date): boolean {
        if (tokenExpirationDate) {
            const minutesToExpiration = this.calculateMinutesToExpiration(tokenExpirationDate);
            if (minutesToExpiration > 2) {
                return false;
            }
            return true;
        }
        return false;
    }

    /** Logs a failure no one logged yet: ApiService logs every request it makes that fails (an axios
     * error), except a 401/403, which the API logged when it answered. */
    private logUnlessLogged(error: any, context: string): void {
        if (!isAuthRejection(error) && error?.isAxiosError !== true) {
            logger(error, context);
        }
    }

    private async getAuthState(resource: TokenResource): Promise<IAuthTokenResultDto> {
        return this.sharedRefreshes.share<IAuthTokenResultDto>("auth",
            state => this.tokenRefreshRequired(this.getTokenExpirationDate(resource, state)) && !this.isPausedAfterUnansweredRefresh(state),
            (state, markRanOut) => this.refreshAuthState(state.account.accountId, stillWanted => this.authTokenCall(state.account, stillWanted), true, markRanOut),
            state => state,
            // Another refresh's state is taken only with a token that needn't be refreshed: the pause
            // after no answer brings none (the third review, 2026-10-28).
            state => !this.tokenRefreshRequired(this.getTokenExpirationDate(resource, state)));
    }

    /** The token call that refreshes an account's Graph, SharePoint, OneDrive and Teams tokens. */
    private authTokenCall(account: IAuthAccount, stillWanted: () => boolean): Promise<IAuthTokenResultDto> {
        return account.sharePointBaseUrl ? apiRefreshTokenRequest(account, stillWanted) : apiSilentTokenRequest(account.accountId, stillWanted);
    }

    requestTokenSilent = (accountId: string): Promise<IAuthTokenResultDto> =>
        this.refreshAuthState(accountId, stillWanted => apiSilentTokenRequest(accountId, stillWanted));

    requestTokenRefresh = (account: IAuthAccount): Promise<IAuthTokenResultDto> =>
        this.refreshAuthState(account?.accountId, stillWanted => apiRefreshTokenRequest(account, stillWanted));

    // A refresh's outcome applies only to the state it started from (final review 2026-10-19,
    // review 2026-10-20): after a sign-out, a new sign-in (of any account) or a newer refresh while
    // the call ran, it returns the state as it is now. Persisting signed the user back in; clearing
    // signed the newer state out. A call that got no answer keeps the state too (review 2026-10-20):
    // it signed the user out and cleared all of local storage when a laptop woke before its network.
    // Any answer signs out, as it always did: a refusal (a 401/403, a sign-in Microsoft refused,
    // another 4xx), and a 5xx, 408 or 429 too: a sign-in kept through an outage gives the bugs it
    // brings time they didn't have (2026-10-27).
    // tracked (a scheduled refresh, held by SharedRefreshes.share): its callers wait for the answer,
    // and a call unanswered after askOnceMoreAfterMs is asked once more (askOnceMore). A call is
    // asked again only while before still holds (stillWanted). markRanOut: it ended without tokens,
    // in the state it started from, after its call ran out its own limit, so the caller after it waits
    // for it first, as for a call that never ended before 2026-10-26 (SharedRefreshes). Not when the
    // call asked again brought tokens, for a run-out after it ended, nor when only the call asked once
    // more ran out (none before 2026-10-26), the state was replaced meanwhile, or it ended when the
    // device went offline, which would have ended a call that never answered then too (the third to
    // sixth reviews, 2026-10-28: the caller after it, even an hour later, waited ten seconds for
    // nothing).
    private async refreshAuthState(accountId: string, call: RefreshCall, tracked: boolean = false, markRanOut: () => void = () => undefined): Promise<IAuthTokenResultDto> {
        const before: IAuthTokenResultDto = this.getPersistedAuthState();
        let ranOut: boolean = false;
        const noteRanOut = (): void => { ranOut = true; };
        const outcome: Promise<RefreshOutcome> = this.askUntilAnswered(accountId, before, call, noteRanOut);
        let ended: RefreshOutcome = null;
        if (tracked) {
            ended = await waitForRefresh(outcome, AuthModule.askOnceMoreAfterMs);
            // Its outcome would be dropped: the state as it is now.
            if (!ended && !this.stillHolds(before, accountId)) {
                return this.getPersistedAuthState();
            }
            // The call is asked once more, and tokens it brings first are its callers'.
            ended = ended ?? await this.unlessBroughtFirst(outcome, this.askOnceMore(accountId, before, call));
        } else {
            ended = await outcome;
        }
        if (ranOut && !("value" in ended) && !wentOffline(ended.error) && this.stillHolds(before, accountId)) {
            markRanOut();
        }
        return this.applyRefreshOutcome(accountId, before, ended);
    }

    /** The first call's outcome, unless the call asked once more brings tokens first. Asking once more
     * only adds a chance: its failure doesn't end the wait, as that would hand out expired tokens or
     * sign out while the first call could still bring tokens. */
    private unlessBroughtFirst(first: Promise<RefreshOutcome>, onceMore: Promise<RefreshOutcome | null>): Promise<RefreshOutcome> {
        return new Promise<RefreshOutcome>(resolve => {
            first.then(resolve);
            onceMore.then(outcome => {
                if (outcome && "value" in outcome) {
                    resolve(outcome);
                }
            });
        });
    }

    /** A scheduled refresh's call asked once more for before, after askOnceMoreAfterMs without an
     * answer (2026-10-28); once per state, so a waiter's
     * refresh joins it. Otherwise they wait for the call's 5-minute limit (before 2026-10-26, for
     * ever). Asked once: run out too, it isn't asked again, as the first call's chain is (review
     * 2026-10-28: asked again, it went on every 5 minutes for nobody). Tokens it brings are kept
     * when they come, also after the first call ended without an answer (applyRefreshOutcome keeps
     * them only while before still holds). Null when its own limit ended it, which doesn't mark the
     * refresh (refreshAuthState). */
    private askOnceMore(accountId: string, before: IAuthTokenResultDto, call: RefreshCall): Promise<RefreshOutcome | null> {
        if (this.askedOnceMore?.before !== before) {
            const outcome: Promise<RefreshOutcome | null> = this.askOnce(accountId, before, call, () => undefined);
            this.askedOnceMore = { before, outcome };
            outcome.then(settled => settled && "value" in settled ? this.applyRefreshOutcome(accountId, before, settled) : undefined)
                .catch(error => logger(error, "AuthModule.ts (askOnceMore)"));
        }
        return this.askedOnceMore.outcome;
    }

    /** The refresh's call, asked again each time its own limit (ApiService) ends it while before still
     * holds, until it ends any other way: ending in the state held would hand out tokens that had
     * expired (before 2026-10-26 the call had no limit, and its callers waited however long it took).
     * Asked again by one refresh at a time for the same state: another whose call runs out meanwhile
     * takes its outcome rather than asking on its own. */
    private async askUntilAnswered(accountId: string, before: IAuthTokenResultDto, call: RefreshCall, markRanOut: () => void): Promise<RefreshOutcome> {
        const outcome: RefreshOutcome | null = await this.askOnce(accountId, before, call, markRanOut);
        if (outcome) {
            return outcome;
        }
        if (this.askingAgain?.before === before) {
            return this.askingAgain.outcome;
        }
        const again: { before: IAuthTokenResultDto; outcome: Promise<RefreshOutcome> } = { before, outcome: this.keepAsking(accountId, before, call, markRanOut) };
        this.askingAgain = again;
        const clear = (): void => {
            if (this.askingAgain === again) {
                this.askingAgain = null;
            }
        };
        again.outcome.then(clear, clear);
        return again.outcome;
    }

    /** askUntilAnswered's call asked again, each time its own limit ends it while before still holds. */
    private async keepAsking(accountId: string, before: IAuthTokenResultDto, call: RefreshCall, markRanOut: () => void): Promise<RefreshOutcome> {
        for (;;) {
            const outcome: RefreshOutcome | null = await this.askOnce(accountId, before, call, markRanOut);
            if (outcome) {
                return outcome;
            }
        }
    }

    /** How the refresh's call ended, or null when its own limit ended it while before still holds.
     * A call its own limit ended is marked (markRanOut). */
    private async askOnce(accountId: string, before: IAuthTokenResultDto, call: RefreshCall, markRanOut: () => void): Promise<RefreshOutcome | null> {
        const outcome: RefreshOutcome = await this.withJwtRetry(accountId, () => call(() => this.stillHolds(before, accountId))).then(value => ({ value }), error => ({ error }));
        const ranOut: boolean = !("value" in outcome) && ranOutOfTime(outcome.error);
        if (ranOut) {
            markRanOut();
        }
        return ranOut && this.stillHolds(before, accountId) ? null : outcome;
    }

    /** Applies how a refresh for accountId ended to before, the state it started from (see
     * refreshAuthState). A sign-out it causes is announced here: the callers of a replacement may all
     * have gone on already. */
    private async applyRefreshOutcome(accountId: string, before: IAuthTokenResultDto, outcome: RefreshOutcome): Promise<IAuthTokenResultDto> {
        let error: any;
        if ("value" in outcome) {
            try {
                if (!this.stillHolds(before, accountId)) {
                    return this.getPersistedAuthState();
                }
                return await this.configureAndPersistRefreshedAuthState(outcome.value);
            } catch (persistError) {
                error = persistError;
            }
        } else {
            error = outcome.error;
        }

        this.logUnlessLogged(error, "AuthModule.ts (refreshAuthState)");

        if (!this.stillHolds(before, accountId)) {
            return this.getPersistedAuthState();
        }
        if (isUnanswered(error)) {
            return this.keepAfterUnansweredRefresh(accountId);
        }
        this.clearPersistedAuthState();
        this.notifySignOut();
        return null;
    }

    /** Whether the token for resource that state holds has expired. */
    private tokenExpired(resource: TokenResource, state: IAuthTokenResultDto): boolean {
        const expiry: Date = this.getTokenExpirationDate(resource, state);
        return !!expiry && +dayjs.utc(expiry) <= Date.now();
    }

    /** Whether the state still belongs to accountId: false once the user signed out, or another
     * account signed in, while a token call for accountId ran. */
    private isStillSignedInAs(accountId: string): boolean {
        return sameAccountId(this.getPersistedAuthState()?.account?.accountId, accountId);
    }

    /** Whether before, the state a call for accountId started from, is still the one signed in: false
     * after a sign-out, a new sign-in (of any account) or a newer refresh, each of which saves a new
     * state. A getter's own tokens are written into the state in place (persistOwnFields). */
    private stillHolds(before: IAuthTokenResultDto, accountId: string): boolean {
        return !!before && this.getPersistedAuthState() === before && sameAccountId(before.account?.accountId, accountId);
    }

    /** Keeps accountId's state after its scheduled refresh got no answer, and starts the pause. */
    private keepAfterUnansweredRefresh(accountId: string): IAuthTokenResultDto {
        this.unansweredRefresh = { accountId, at: Date.now() };
        return this.getPersistedAuthState();
    }

    /** Whether the scheduled refresh for state's account got no answer less than
     * unansweredRefreshPauseMs ago. A clock set back meanwhile ends the pause, never stretches it
     * (review 2026-10-20). */
    private isPausedAfterUnansweredRefresh(state: IAuthTokenResultDto): boolean {
        const paused: { accountId: string; at: number } = this.unansweredRefresh;
        if (!paused || !sameAccountId(paused.accountId, state?.account?.accountId)) {
            return false;
        }
        const elapsed: number = Date.now() - paused.at;
        return elapsed >= 0 && elapsed < AuthModule.unansweredRefreshPauseMs;
    }

    /**
     * A token call that answers 401 gets one renewed Docubird JWT and is asked once more (audit
     * 2026-10-19): the client can't tell a JWT the API found expired (a clock behind, a refresh
     * that failed in passing) from one without an account, and RefreshJwt renews only the first.
     * A 403 (another account's tokens), a renewal RefreshJwt refuses or a second 401 is thrown to the
     * caller. A renewal that fails for a moment (no answer, a 5xx) hands back the JWT the API just
     * refused: that is not asked again, as it could only get the same 401, and the call fails like
     * one that got no answer, which keeps the sign-in (review 2026-10-20, as before 2026-10-26).
     * Nor is a call for accountId asked again once another account (or nobody) holds the state: the
     * renewal would be the other account's JWT (review 2026-10-20).
     */
    private async withJwtRetry<T>(accountId: string, call: () => Promise<T>): Promise<T> {
        try {
            return await call();
        } catch (error) {
            if (error?.response?.status !== 401 || !this.isStillSignedInAs(accountId)) {
                throw error;
            }
            // Refused with a JWT another call renewed meanwhile: ask again with that one, without
            // renewing it once more (follow-up 2026-10-20).
            const sent: string | undefined = bearerToken(error?.config?.headers);
            const current: string = this.getPersistedAuthState()?.docubirdToken?.token;
            if (sent && current && sent !== current) {
                return await call();
            }
            const refused: string = sent || current;
            const renewed: string = await this.getDocubirdToken(true);
            if (!renewed) {
                throw error;
            }
            if (renewed === refused) {
                throw failedJwtRenewal();
            }
            return await call();
        }
    }

    /**
     * Writes a getter's own token onto the state as it is NOW, not the copy it read before waiting
     * for the API (audit 2026-10-19): that copy would undo a Graph refresh, a sign-out or another
     * account's sign-in that happened meanwhile. Null (and nothing written) when the state is gone
     * or belongs to another account.
     */
    private async persistOwnFields(accountId: string, apply: (current: IAuthTokenResultDto) => void): Promise<IAuthTokenResultDto | null> {
        const current: IAuthTokenResultDto = this.getPersistedAuthState();
        if (!sameAccountId(current?.account?.accountId, accountId)) {
            return null;
        }
        apply(current);
        await this.persistAuthState(current);
        return current;
    }

    private async configureAndPersistRefreshedAuthState(refreshedAuthState: IAuthTokenResultDto): Promise<IAuthTokenResultDto> {
        if (refreshedAuthState) {
            const persistedAuthState: IAuthTokenResultDto = this.getPersistedAuthState();
            refreshedAuthState.flowToken = persistedAuthState?.flowToken;
            refreshedAuthState.flowTokenExpiresOn = persistedAuthState?.flowTokenExpiresOn;
            refreshedAuthState.sharePointAdminToken = persistedAuthState?.sharePointAdminToken;
            refreshedAuthState.sharePointAdminTokenExpiresOn = persistedAuthState?.sharePointAdminTokenExpiresOn;
            // refreshedAuthState.mailSendToken = persistedAuthState?.mailSendToken;
            // refreshedAuthState.mailSendTokenExpiresOn = persistedAuthState?.mailSendTokenExpiresOn;
            // refreshedAuthState.mailboxToken = persistedAuthState?.mailboxToken;
            // refreshedAuthState.mailboxTokenExpiresOn = persistedAuthState?.mailboxTokenExpiresOn;

            await this.persistAuthState(refreshedAuthState);
            // Consent lives in the Graph token, not in Redux: tell Copilot's availability when a
            // refresh, scheduled or forced, changes it.
            if (refreshedAuthState.copilotScopesGranted !== persistedAuthState?.copilotScopesGranted) {
                notifyCopilotAvailabilityChanged();
            }
        }
        return refreshedAuthState;
    }

    async signIn(): Promise<IAuthTokenResultDto | null> {
        // return await this.requestTokenSilent("7e9992b2-a3bf-4019-a27d-033d00ce6598.3f50a620-6f6b-4a76-a8fe-57fce2ef9e1d");
        return await this.getAuthState(TokenResource.All);
    }

    async signOut(): Promise<void> {
        this.clearPersistedAuthState();
        // Asked for: always announced, as a new episode.
        this.signOutNotified = false;
        this.notifySignOut();
    }

    // A request found no signed-in state (AxiosRetryUtil's interceptor): announced once per episode,
    // and cleared only then. Calling signOut() there restarted the episode on every request, so one
    // sign-out cleared localStorage and reset the app about six times (final review 2026-10-19).
    signOutDetected(): void {
        if (this.signOutNotified) {
            return;
        }
        this.clearPersistedAuthState();
        this.notifySignOut();
    }

    // Account-bound JWT (docs/superpowers/specs/2026-10-13-copilot-chat-design.md §1.1): a 401 or
    // 403 from a token endpoint means the Docubird JWT doesn't belong to this account, or carries
    // no account (one issued before the API bound it; D2: no grace window). Only an interactive
    // sign-in fixes that, so take the path a failed refresh takes: clear the persisted state, then
    // let the shell show the sign-in. The refusal answers for snapshot, the state the call started
    // from (review 2026-10-19, 2026-10-20): a newer state (another account's sign-in, a new
    // sign-in, a newer refresh) stays signed in. Returns true for every 401/403, signed out or not.
    private signOutOnAuthRejection(error: any, snapshot: IAuthTokenResultDto): boolean {
        if (!isAuthRejection(error)) {
            return false;
        }

        const current: IAuthTokenResultDto = this.getPersistedAuthState();
        if (!current?.account || current === snapshot) {
            this.clearPersistedAuthState();
            this.notifySignOut();
        }
        return true;
    }

    async getGraphAccessToken(): Promise<string> {
        const authState: IAuthTokenResultDto = await this.getAuthState(TokenResource.Graph);
        if (authState) {
            return authState.graphToken;
        }
        else {
            this.notifySignOut();
            return null;
        }
    }

    /**
     * One forced refresh of the Graph token, for Copilot. Entra puts the Copilot scopes only into
     * tokens issued after the tenant's consent, and a page holds its token for up to an hour. Two
     * callers: copilotClient answers Copilot's 401 with one fresh token before it gives up, and
     * CopilotConsentNotice asks for one when a tenant admin comes back from Microsoft's consent page.
     * - The same branch as getAuthState: RefreshTokenRequest when the account has a
     *   sharePointBaseUrl, SilentTokenRequest otherwise.
     * - Waits (at most REFRESH_WAIT_MS) for a refresh already in progress; while it runs, a getter
     *   whose token is due joins it.
     * - A failed refresh never clears the auth state: a failed Copilot call must not sign the user
     *   out. Phase 1's account-binding refusal (a 401 or 403 from the token endpoint itself) is not a
     *   failed refresh: it signs out here as it does for every token call.
     * - Keeps nothing when the user signed out, or another account signed in, while it ran.
     * - Stops waiting for a token call after 30 seconds and resolves undefined; a token that still
     *   comes is kept when it is newer and the account is still signed in.
     * - At most once per Graph token (identified by graphTokenExpiresOn), remembered in localStorage
     *   so a reload doesn't buy another one. When another tab already refreshed this token, its newer
     *   token in localStorage is taken instead (this page keeps its own copy in memory).
     * Resolves with the new Graph token, or undefined when nothing was refreshed.
     */
    async forceGraphRefresh(): Promise<string | undefined> {
        // A second caller for the same account while one runs shares it (audit 2026-10-19); another
        // account's caller, after a sign-out, starts its own (review 2026-10-20).
        const accountId: string = this.getPersistedAuthState()?.account?.accountId;
        if (this.forcedGraphRefresh && sameAccountId(this.forcedGraphRefresh.accountId, accountId)) {
            return this.forcedGraphRefresh.promise;
        }
        const running: { accountId: string; promise: Promise<string | undefined> } = { accountId, promise: this.forceGraphRefreshOnce() };
        this.forcedGraphRefresh = running;
        try {
            return await running.promise;
        } finally {
            if (this.forcedGraphRefresh === running) {
                this.forcedGraphRefresh = null;
            }
        }
    }

    private async forceGraphRefreshOnce(): Promise<string | undefined> {
        const before: IAuthTokenResultDto = this.getPersistedAuthState();
        const running: Promise<void> | null = this.sharedRefreshes.whenLatestEnds("auth", before?.account?.accountId);
        if (running) {
            // A refresh already running may bring a new token: take that instead of forcing another.
            await waitForRefresh(running);
            const after: IAuthTokenResultDto = this.getPersistedAuthState();
            if (after?.graphToken && sameAccountId(after.account?.accountId, before?.account?.accountId)
                && this.graphTokenKey(after) !== this.graphTokenKey(before)) {
                return after.graphToken;
            }
        }
        const authState: IAuthTokenResultDto = this.getPersistedAuthState();
        if (!authState?.account) {
            return undefined;
        }

        const tokenKey: string = this.graphTokenKey(authState);
        if (this.readForcedGraphRefresh() === tokenKey) {
            return this.adoptNewerGraphToken(authState);
        }
        this.rememberForcedGraphRefresh(tokenKey);

        // Held as the account's refresh while it runs: a getter that needs a token joins it.
        const accountId: string = authState.account.accountId;
        return this.sharedRefreshes.track("auth", accountId, () => this.runForcedGraphRefresh(authState, accountId)).run;
    }

    private async runForcedGraphRefresh(authState: IAuthTokenResultDto, accountId: string): Promise<string | undefined> {
        try {
            const refreshedAuthState: IAuthTokenResultDto = await this.withinForcedRefreshTime(
                this.withJwtRetry(accountId, () => this.authTokenCall(authState.account, () => this.isStillSignedInAs(accountId))),
                late => this.keepLateGraphRefresh(late, accountId));
            if (!refreshedAuthState?.graphToken || !this.isStillSignedInAs(accountId)) {
                // Nothing came back, or the user signed out (or another account signed in) meanwhile:
                // persisting now would sign them back in.
                return undefined;
            }
            await this.configureAndPersistRefreshedAuthState(refreshedAuthState);
            return refreshedAuthState.graphToken;
        } catch (error) {
            // Phase 1: a 401/403 from the token endpoint signs out (not logged: the API logged it);
            // any other failure leaves the state. A sign-in Microsoft refused too, as before
            // 2026-10-26: the tokens held still work for everything but Copilot, and the next
            // scheduled refresh signs out; signing out here would end all of it for Copilot's sake.
            if (!this.signOutOnAuthRejection(error, authState)) {
                this.logUnlessLogged(error, "AuthModule.ts (forceGraphRefresh)");
            }
            return undefined;
        }
    }

    /**
     * The forced refresh's token call, or a rejection once forcedGraphRefreshTimeoutMs has passed
     * (final review 2026-10-19): a call that never answered held Copilot's retry and the consent
     * check, every later forced refresh shared it, and the token getters that needed a refresh
     * waited for it. An answer that still comes goes to onLateAnswer; a late failure to no one.
     */
    private withinForcedRefreshTime<T>(call: Promise<T>, onLateAnswer: (answer: T) => void): Promise<T> {
        let timer: ReturnType<typeof setTimeout>;
        const timeout: Promise<never> = new Promise<never>((_, reject) => {
            timer = setTimeout(() => {
                call.then(onLateAnswer, () => undefined);
                reject(new Error(`The forced Graph refresh got no answer within ${AuthModule.forcedGraphRefreshTimeoutMs / 1000} s.`));
            }, AuthModule.forcedGraphRefreshTimeoutMs);
        });
        return Promise.race([call, timeout]).finally(() => clearTimeout(timer));
    }

    /**
     * A forced refresh that answered after forceGraphRefresh gave up (review 2026-10-19): a slow
     * refresh can still succeed (retries alone wait 15 s; the phase 0 probe saw 55 s after consent),
     * and its token may carry the Copilot scopes the admin just granted. Kept, with the availability
     * signal, while the account still holds the state and the token is newer than the one saved.
     */
    private async keepLateGraphRefresh(late: IAuthTokenResultDto, accountId: string): Promise<void> {
        try {
            if (late?.graphToken && this.isStillSignedInAs(accountId)
                && this.graphTokenExpiry(late) > this.graphTokenExpiry(this.getPersistedAuthState())) {
                await this.configureAndPersistRefreshedAuthState(late);
            }
        } catch (error) {
            logger(error, "AuthModule.ts (forceGraphRefresh)");
        }
    }

    // The Graph token's expiry in ms, or NaN when it has none or it can't be read.
    private graphTokenExpiry(authState: IAuthTokenResultDto): number {
        return authState?.graphTokenExpiresOn ? +dayjs.utc(authState.graphTokenExpiresOn) : NaN;
    }

    private graphTokenKey(authState: IAuthTokenResultDto): string {
        const expiry: number = this.graphTokenExpiry(authState);
        return isNaN(expiry) ? "none" : String(expiry);
    }

    /** The guard blocked a refresh of this page's token: if localStorage itself (not this page's copy
     * in memory) holds a NEWER Graph token for the same account, another tab refreshed it. A copy
     * that differs only by being older is left alone (audit 2026-10-19). */
    private adoptNewerGraphToken(authState: IAuthTokenResultDto): string | undefined {
        const stored: IAuthTokenResultDto = this.tryGetAuthState(TokenStorage.Local);
        if (stored?.graphToken && sameAccountId(stored.account?.accountId, authState.account.accountId)
            && this.graphTokenExpiry(stored) > this.graphTokenExpiry(authState)) {
            this.globalAuthState = stored;
            if (stored.copilotScopesGranted !== authState.copilotScopesGranted) {
                notifyCopilotAvailabilityChanged();
            }
            return stored.graphToken;
        }
        return undefined;
    }

    private readForcedGraphRefresh(): string | null {
        try {
            return localStorage.getItem(AuthModule.ForcedGraphRefreshKey);
        } catch (error) {
            return this.lastForcedGraphRefresh;
        }
    }

    private rememberForcedGraphRefresh(tokenKey: string): void {
        this.lastForcedGraphRefresh = tokenKey;
        try {
            localStorage.setItem(AuthModule.ForcedGraphRefreshKey, tokenKey);
        } catch (error) {
            // Without storage the guard holds for this page only.
        }
    }

    async getTeamsAccessToken(): Promise<string> {
        const authState: IAuthTokenResultDto = await this.getAuthState(TokenResource.Teams);
        if (authState) {
            return authState.teamsToken;
        }
        else {
            this.notifySignOut();
            return null;
        }
    }

    async getSharePointAccessToken(): Promise<string> {
        const authState: IAuthTokenResultDto = await this.getAuthState(TokenResource.SharePoint);
        if (authState) {
            return authState.sharePointToken;
        }
        else {
            this.notifySignOut();
            return null;
        }
    }

    async getOneDriveAccessToken(): Promise<string> {
        const authState: IAuthTokenResultDto = await this.getAuthState(TokenResource.OneDrive);
        if (authState) {
            return authState.oneDriveToken;
        }
        else {
            this.notifySignOut();
            return null;
        }
    }

    // forceRefresh (the caller's token was refused): a refresh that brought another token already,
    // one the caller joined included, is what it asked for.
    async getFlowAccessToken(forceRefresh?: boolean): Promise<string> {
        const tokenAtCall: string = this.getPersistedAuthState()?.flowToken;
        return this.sharedRefreshes.share<string>("flow",
            state => forceRefresh
                ? state.flowToken === tokenAtCall
                : this.tokenRefreshRequired(this.getTokenExpirationDate(TokenResource.Flow, state)),
            (state, markRanOut) => this.refreshFlowToken(state, markRanOut),
            state => state?.flowToken);
    }

    /** The state as it is now, when accountId still holds it; null otherwise. What a getter whose
     * call brought nothing new hands back: the token of the state it started from was another
     * account's after a switch, or one after a sign-out (audit 2026-10-27). */
    private stateHeldBy(accountId: string): IAuthTokenResultDto | null {
        const current: IAuthTokenResultDto = this.getPersistedAuthState();
        return sameAccountId(current?.account?.accountId, accountId) ? current : null;
    }

    // markRanOut: its call ran out its time limit (SharedRefreshes: a caller waits for it first).
    private async refreshFlowToken(snapshot: IAuthTokenResultDto, markRanOut: () => void): Promise<string> {
        let authState: IAuthTokenResultDto = null;
        try {
            const flowTokenResponse = await this.withJwtRetry(snapshot.account?.accountId, () => apiAquireFlowToken(snapshot, () => this.isStillSignedInAs(snapshot.account?.accountId)));
            if (flowTokenResponse?.flowToken) {
                authState = await this.persistOwnFields(snapshot.account?.accountId, current => {
                    current.flowToken = flowTokenResponse.flowToken;
                    current.flowTokenExpiresOn = flowTokenResponse.flowTokenExpiresOn;
                });
            }
            else {
                authState = this.stateHeldBy(snapshot.account?.accountId);
            }
        } catch (error) {
            if (ranOutOfTime(error)) {
                markRanOut();
            }
            if (!this.signOutOnAuthRejection(error, snapshot)) {
                this.logUnlessLogged(error, "AuthModule.ts (getFlowAccessToken)");
                authState = this.stateHeldBy(snapshot.account?.accountId);
            }
        }
        return authState?.flowToken;
    }

    async getSharePointAdminAccessToken(forceRefresh?: boolean): Promise<string> {
        let authState: IAuthTokenResultDto = this.getPersistedAuthState();
        if (authState) {
            if (forceRefresh || this.tokenRefreshRequired(this.getTokenExpirationDate(TokenResource.SharePointAdmin, authState))) {
                const snapshot: IAuthTokenResultDto = authState;
                authState = null;
                try {
                    const sharePointAdminTokenResponse: IAuthTokenResultDto = await this.withJwtRetry(snapshot.account?.accountId, () => apiAquireSharePointAdminToken(snapshot, () => this.isStillSignedInAs(snapshot.account?.accountId)));
                    if (sharePointAdminTokenResponse?.sharePointAdminToken) {
                        authState = await this.persistOwnFields(snapshot.account?.accountId, current => {
                            current.sharePointAdminToken = sharePointAdminTokenResponse.sharePointAdminToken;
                            current.sharePointAdminTokenExpiresOn = sharePointAdminTokenResponse.sharePointAdminTokenExpiresOn;
                        });
                    }
                    else {
                        authState = this.stateHeldBy(snapshot.account?.accountId);
                    }
                } catch (error) {
                    if (!this.signOutOnAuthRejection(error, snapshot)) {
                        this.logUnlessLogged(error, "AuthModule.ts (getSharePointAdminAccessToken)");
                        authState = this.stateHeldBy(snapshot.account?.accountId);
                    }
                }
            }
        }
        return authState?.sharePointAdminToken;
    }

    async getMailSendAccessToken(forceRefresh?: boolean): Promise<string> {
        const tokenAtCall: string = this.getPersistedAuthState()?.mailSendToken;
        return this.sharedRefreshes.share<string>("mailSend",
            // A missing token (or a token without an expiry) must count as "refresh required":
            // tokenRefreshRequired(undefined) is false, which previously turned an absent token into
            // a silent `undefined` return and a "Bearer undefined" Graph call.
            state => forceRefresh
                ? state.mailSendToken === tokenAtCall
                : !state.mailSendToken || !state.mailSendTokenExpiresOn
                    || this.tokenRefreshRequired(this.getTokenExpirationDate(TokenResource.MailSend, state)),
            (state, markRanOut) => this.refreshMailSendToken(state, markRanOut),
            state => state?.mailSendToken || undefined,
            // Not the token the caller found refused (forced), nor an expired or missing one.
            state => !!state.mailSendToken && !(forceRefresh && state.mailSendToken === tokenAtCall) && !this.tokenExpired(TokenResource.MailSend, state));
    }

    private async refreshMailSendToken(snapshot: IAuthTokenResultDto, markRanOut: () => void): Promise<string> {
        let authState: IAuthTokenResultDto = null;
        try {
            const mailSendTokenResponse: IAuthTokenResultDto = await this.withJwtRetry(snapshot.account?.accountId, () => apiAquireMailSendToken(snapshot));
            if (mailSendTokenResponse?.mailSendToken) {
                // Merged into whatever is persisted NOW (persistOwnFields), never into a state
                // that was signed out, or belongs to another account, meanwhile.
                authState = await this.persistOwnFields(snapshot.account?.accountId, current => {
                    current.mailSendToken = mailSendTokenResponse.mailSendToken;
                    current.mailSendTokenExpiresOn = mailSendTokenResponse.mailSendTokenExpiresOn;
                });
            }
            else {
                authState = this.stateHeldBy(snapshot.account?.accountId);
            }
        } catch (error) {
            if (ranOutOfTime(error)) {
                markRanOut();
            }
            if (!this.signOutOnAuthRejection(error, snapshot)) {
                this.logUnlessLogged(error, "AuthModule.ts (getMailSendAccessToken)");
                authState = this.stateHeldBy(snapshot.account?.accountId);
            }
        }
        return authState?.mailSendToken || undefined;
    }

    async getDocubirdToken(refreshRequired?: boolean): Promise<string> {
        const authState: IAuthTokenResultDto = this.getPersistedAuthState();
        const token: string = authState?.docubirdToken?.token;
        if (!token || !(refreshRequired === true || this.tokenRefreshRequired(new Date(authState.docubirdToken.expirationDate)))) {
            return token;
        }
        // One renewal per JWT: every caller holding the same JWT shares it, forced or not
        // (follow-up 2026-10-20: N requests near expiry made N renewals and N saves).
        if (this.jwtRenewal?.token === token) {
            return this.jwtRenewal.promise;
        }
        const renewal: { token: string; promise: Promise<string> } = { token, promise: this.renewDocubirdToken(authState) };
        this.jwtRenewal = renewal;
        const clear = (): void => {
            if (this.jwtRenewal === renewal) {
                this.jwtRenewal = null;
            }
        };
        renewal.promise.then(clear, clear);
        return renewal.promise;
    }

    // A renewal ending after a sign-out or another account's sign-in was the old account's: hand
    // back what is signed in now. Its null used to make the request interceptor sign the new account
    // out (audit 2026-10-19).
    private async renewDocubirdToken(snapshot: IAuthTokenResultDto): Promise<string> {
        try {
            const jwtAuthTokenReponse: IJwtAuthTokenResult = await refreshJwt(snapshot.docubirdToken.token);
            if (!this.isStillSignedInAs(snapshot.account?.accountId)) {
                return this.getPersistedAuthState()?.docubirdToken?.token;
            }

            if (!jwtAuthTokenReponse) {
                // Refused: a sign-out, unless the state was replaced meanwhile (the same account
                // signed in again, a refresh saved a newer JWT) and holds a JWT of its own (review
                // 2026-10-20).
                const current: IAuthTokenResultDto = this.getPersistedAuthState();
                return current === snapshot ? null : current?.docubirdToken?.token ?? null;
            }

            const persisted: IAuthTokenResultDto = await this.persistOwnFields(snapshot.account?.accountId, current => {
                current.docubirdToken = jwtAuthTokenReponse;
            });
            return persisted?.docubirdToken?.token;
        } catch (error) {
            this.logUnlessLogged(error, "AuthModule.ts (getDocubirdToken)");
            if (!this.isStillSignedInAs(snapshot.account?.accountId)) {
                return this.getPersistedAuthState()?.docubirdToken?.token;
            }
        }
        return snapshot.docubirdToken.token;
    }

    getDocubirdTokenCached(): string {
        return this.getPersistedAuthState()?.docubirdToken?.token;
    }

    async getNintexAccessToken(client_id: string, client_secret: string, forceRefresh: boolean = false): Promise<string> {
        let authState: IAuthTokenResultDto = this.getPersistedAuthState();

        if (authState) {
            if (forceRefresh || this.tokenRefreshRequired(this.getTokenExpirationDate(TokenResource.Nintex, authState))) {
                try {
                    let authUrl: URL = new URL(`https://eu.nintex.io/authentication/v1/token`);

                    const body: ITokenRequest = {
                        client_id,
                        client_secret,
                        grant_type: "client_credentials"
                    }

                    const nintexTokenResponse: ITokenResponse = (await axiosRetryPost(authUrl.href, body, { headers: { 'Content-Type': 'application/json' } }))?.data;

                    if (nintexTokenResponse?.access_token) {
                        authState = await this.persistOwnFields(authState.account?.accountId, current => {
                            current.nintexToken = nintexTokenResponse.access_token;
                            current.nintexTokenExpiresOn = new Date(nintexTokenResponse.expires_at);
                        });
                    }
                } catch (error) {
                    logger(error, "AuthModule.ts (getMailSendAccessToken)");
                }
            }
        }
        return authState?.nintexToken;
    }

    async getIsTessAuthenticated(forceRefresh: boolean = false): Promise<boolean> {
        let authState: IAuthTokenResultDto = this.getPersistedAuthState();
        if (forceRefresh && authState) {
            try {
                const isTessAuthenticated: boolean = await apiCheckIsTessAuthenticated(authState);
                authState = await this.persistOwnFields(authState.account?.accountId, current => {
                    current.isTessAuthenticated = isTessAuthenticated;
                });
            } catch (error) {
                logger(error, "AuthModule.ts (getIsTessAuthenticated)");
            }
        }
        return authState?.isTessAuthenticated;
    }

    // async getMailboxAccessToken(forceRefresh?: boolean): Promise<string> {
    //     if (this.isMailboxAccessTokenRefreshInProgress === true) await this.waitOnRefresh(TokenResource.Mailbox);
    //     let authState: IAuthTokenResultDto = this.getPersistedAuthState();
    //     if (authState && authState.account?.accountId) {
    //         if (forceRefresh || this.tokenRefreshRequired(this.getTokenExpirationDate(TokenResource.Mailbox, authState))) {
    //             this.isMailboxAccessTokenRefreshInProgress = true;
    //             try {
    //                 const mailboxTokenResponse: IAuthTokenResultDto = await apiAquireMailboxToken(authState);
    //                 if (mailboxTokenResponse?.mailboxToken && mailboxTokenResponse?.mailboxUsername) {
    //                     authState.mailboxToken = mailboxTokenResponse?.mailboxToken;
    //                     authState.mailboxTokenExpiresOn = mailboxTokenResponse?.mailboxTokenExpiresOn;
    //                     authState.mailboxAccountId = mailboxTokenResponse?.mailboxAccountId;
    //                     authState.mailboxUsername = mailboxTokenResponse?.mailboxUsername;
    //                     this.persistAuthState(authState);
    //                 }
    //             } catch (error) {
    //                 logger(error, "AuthModule.ts (getMailboxAccessToken)");
    //             }
    //             this.isMailboxAccessTokenRefreshInProgress = false;
    //         }
    //     }
    //     return authState?.mailboxToken;
    // }

    async getAccount(refreshTokensIfRequired: boolean = true): Promise<IAuthAccount> {
        const authState: IAuthTokenResultDto = refreshTokensIfRequired ? await this.getAuthState(TokenResource.All) : this.getPersistedAuthState();
        return authState?.account;
    }

    async getAuthData(): Promise<IAuthTokenResultDto | null> {
        const authState: IAuthTokenResultDto = await this.getAuthState(TokenResource.All);
        if (authState) {
            return authState;
        }
        else {
            this.notifySignOut();
            return null;
        }
    }

    getMinutesToExpiration(resource: TokenResource): number {
        return this.calculateMinutesToExpiration(this.getTokenExpirationDate(resource, this.getPersistedAuthState()));
    }

    /** Whether the token held for resource has expired: not without one, or without its expiry. */
    hasTokenExpired(resource: TokenResource): boolean {
        return this.tokenExpired(resource, this.getPersistedAuthState());
    }

    async initiateDialogAuthFlow(loginCallback: (authResult: IAuthTokenResultDto) => Promise<void>, resource?: TokenResource, useContext?: boolean): Promise<void> {
        let loginDialog: Office.Dialog;

        const processLoginMessage = (args: { message: string; origin: string; }) => {
            const messageFromDialog: DialogMessageResponse = JSON.parse(args.message);
            if (messageFromDialog.status === DialogMessageStatus.Success) {
                if (resource === TokenResource.Flow) {
                    if (messageFromDialog.result?.flowToken) {
                        let authState: IAuthTokenResultDto = this.getPersistedAuthState();
                        if (authState) {
                            authState.flowToken = messageFromDialog.result.flowToken;
                            authState.flowTokenExpiresOn = messageFromDialog.result.flowTokenExpiresOn;
                            this.persistAuthState(authState);
                        }
                    }
                }
                // else if (resource === TokenResource.Mailbox) {
                //     if (messageFromDialog.result?.mailboxToken && messageFromDialog.result?.mailboxUsername) {
                //         let authState: IAuthTokenResultDto = this.getPersistedAuthState();
                //         if (authState) {
                //             authState.mailboxToken = messageFromDialog.result.mailboxToken;
                //             authState.mailboxTokenExpiresOn = messageFromDialog.result.mailboxTokenExpiresOn;
                //             authState.mailboxAccountId = messageFromDialog.result.mailboxAccountId;
                //             authState.mailboxUsername = messageFromDialog.result.mailboxUsername;
                //             this.persistAuthState(authState);
                //         }
                //     }
                // }
                else if (resource === TokenResource.Tess) {
                    let authState: IAuthTokenResultDto = this.getPersistedAuthState();
                    if (authState) {
                        authState.isTessAuthenticated = messageFromDialog.result.isTessAuthenticated;
                        this.persistAuthState(authState);
                    }
                }
                else {
                    this.persistAuthState(messageFromDialog.result);
                }
                loginCallback(messageFromDialog.result);
                loginDialog.close();
            }
            else {
                loginCallback(null);
                loginDialog.close();
            }
        };

        const processLoginDialogEvent = (arg) => {
            this.processDialogEvent(arg, loginCallback);
            loginDialog.close();
        };

        return new Promise((resolve, reject) => {
            let dialogLoginUrl: URL = new URL(process.env.baseUri + "/auth.html");

            const isInContentManagerMode: boolean = generalModule.isInConentMangerMode();
            if (isInContentManagerMode) {
                dialogLoginUrl.searchParams.append(shared.params.app, "content_manager");
            }

            if (resource) {
                dialogLoginUrl.searchParams.append(shared.params.resource, resource.toString());

                const accountDetails: IAuthAccount = this.getPersistedAuthState()?.account;
                dialogLoginUrl.searchParams.append(shared.params.username, accountDetails?.username);
                dialogLoginUrl.searchParams.append(shared.params.tenantId, accountDetails?.tenantId);
                dialogLoginUrl.searchParams.append(shared.params.accountId, accountDetails?.uniqueId);
            }
            else if (!isInContentManagerMode
                && generalModule.getIsOfficeJSAvailable()
                && Office.context.requirements.isSetSupported("Mailbox", "1.5")
                && Office.context.mailbox?.userProfile?.emailAddress) {
                dialogLoginUrl.searchParams.append(shared.params.username, Office.context.mailbox.userProfile.emailAddress);
            }

            Office.context.ui.displayDialogAsync(
                dialogLoginUrl.href,
                { height: 50, width: 35, promptBeforeOpen: false },
                (result) => {
                    if (result.status === Office.AsyncResultStatus.Failed) {
                        console.warn("Office.AsyncResultStatus.Failed: " + `${result.error.code} ${result.error.message}`);
                        reject(result.error)
                    }
                    else {
                        loginDialog = result.value;
                        loginDialog.addEventHandler(Office.EventType.DialogMessageReceived, processLoginMessage);
                        loginDialog.addEventHandler(Office.EventType.DialogEventReceived, processLoginDialogEvent);
                        resolve();
                    }
                }
            );
        });
    }

    private processDialogEvent(arg: { error: number, type: string }, callback: (authResult: IAuthTokenResultDto) => Promise<void>): void {

        switch (arg.error) {
            case 12002:
                // displayError('The dialog box has been directed to a page that it cannot find or load, or the URL syntax is invalid.');
                break;
            case 12003:
                // displayError('The dialog box has been directed to a URL with the HTTP protocol. HTTPS is required.');
                break;
            case 12006:
                // 12006 means that the user closed the dialog instead of waiting for it to close.
                // It is not known if the user completed the login or logout, so assume the user is
                // logged out and revert to the app's starting state. It does no harm for a user to
                // press the login button again even if the user is logged in.

                callback(null);

                break;
            default:
                // displayError('Unknown error in dialog box.');
                break;
        }
    };

    public setTenantApiKey(apiKey: string) {
        this.tenantApiKey = apiKey;
    }

    public getTenantApiKey() {
        return this.tenantApiKey;
    }

    public getCypherSecret() {
        return AuthModule.CypherSecret;
    }

    // Idempotent (audit 2026-10-19): the key is built from the base each time, never appended to
    // the current one, so a second call can't move the sign-in to "AuthState.a@x.a@x".
    public registerMailboxAddress(address: string) {
        if (address?.trim()) {
            AuthModule.AuthStateKey = `${AuthModule.BaseAuthStateKey}.${address}`;
        }
    }
}

const authModule = new AuthModule();
export default authModule;